/* eslint-disable eslint-comments/disable-enable-pair */
/* eslint-disable @typescript-eslint/no-unnecessary-condition */

import Product from '../../domains/Product'
import QuinceApiError from '../../domains/quince-api-error'
import AuthUtil from '../../utils/authUtil'
import { storefrontCartServicesHttpClient } from '../../utils/HttpClient'
import { formatToYYYYMMDD } from '../cartCheckoutService/cartCheckoutService.helper'
import { updateNewCheckoutInLocalStorage } from '../checkoutService/newCheckoutService.helper'
import {
  fetchAndEnrichProducts,
  fetchAndEnrichProductsByInternalProductIdsUsingBatching,
} from '../productService/fetchAndEnrichProducts'

import {
  getDataWithProductAndInventory,
  removeCartIdFromLocalStorage,
  setCartAuthCodeFromHeader,
} from './cart.service.helper'
import { Cart, GiftingDetails, LineItems, TBuyNowApiResponse } from './cart.service.types'

export const getCartWithId = async (cartId: string): Promise<any> => {
  try {
    const res = await storefrontCartServicesHttpClient.get(`/carts/${cartId}`)
    setCartAuthCodeFromHeader(res)
    const { data } = res
    return await getDataWithProductAndInventory(data)
  } catch (e) {
    removeCartIdFromLocalStorage()
    console.error('Failed while fetching getCartWithId(), API - `/carts/${cartId}`', e)
  }
  return {}
}

interface GetProductDataParams {
  productIds: (number | string)[]
  useBatching: boolean
}

export const getProductData = async ({ productIds, useBatching }: GetProductDataParams): Promise<Product[]> => {
  if (useBatching) {
    return await fetchAndEnrichProductsByInternalProductIdsUsingBatching({
      internalProductIds: productIds,
      useLite: true,
    })
  }
  return await fetchAndEnrichProducts({
    internalProductIds: productIds,
    useLite: true,
  })
}

export const getIsCartActive = async (): Promise<any> => {
  try {
    const userSession = await AuthUtil.getUserSession()
    if (!userSession?.idToken?.jwtToken) return
    const res = await storefrontCartServicesHttpClient.get(`/carts/active`, {
      headers: {
        Authorization: `Bearer ${userSession?.idToken?.jwtToken}`,
      },
    })
    setCartAuthCodeFromHeader(res)
    const { data } = res
    return await getDataWithProductAndInventory(data)
  } catch (e) {
    console.error('Failed while fetching getIsCartActive(), API - `/carts/active`', e)
    // throw e
  }
}

export const mergeCustomerCart = async (previousCartId: string): Promise<any> => {
  try {
    const userSession = await AuthUtil.getUserSession()
    const { data } = await storefrontCartServicesHttpClient.post(`/carts/${previousCartId}/merge-customer-cart`, {
      headers: {
        Authorization: `Bearer ${userSession?.idToken?.jwtToken}`,
      },
    })
    return await getDataWithProductAndInventory(data?.second)
  } catch (e) {
    console.error('Failed while fetching mergeCustomerCart(), API - `/merge-customer-cart`', e)
    return {}
  }
}

export const getCarts = async (
  lineItems: LineItems,
  previousCartId?: string,
  isDOBRequired?: boolean,
  dateOfBirth?: string,
): Promise<Cart | undefined> => {
  try {
    const requestBody = {
      lineItems,
      ...(isDOBRequired && dateOfBirth && { customerDateOfBirth: formatToYYYYMMDD(dateOfBirth) }),
    }

    const res = await storefrontCartServicesHttpClient.post(
      previousCartId ? `/carts?previousCartId=${previousCartId}` : `/carts`,
      requestBody,
    )
    setCartAuthCodeFromHeader(res)
    const { data } = res
    return await getDataWithProductAndInventory(data)
  } catch (e) {
    console.error('Failed while fetching getCarts(), API - `/carts`', e)
  }
  return
}

export const getPreviousCarts = async (prevCartId: string): Promise<Cart | undefined> => {
  try {
    const res = await storefrontCartServicesHttpClient.post(`/carts/create-cart-if-possible/${prevCartId}`, {})
    setCartAuthCodeFromHeader(res)
    const { data } = res
    if (data.createdCart) {
      return await getDataWithProductAndInventory(data.cart)
    }
  } catch (e) {
    console.error('Failed while fetching getPreviousCarts(), API - `/carts/create-cart-if-possible/${prevCartId}`', e)
  }
}

export const updateItemQuantity = async (
  cartId: string,
  lineItems: {
    lineItemId: string | null | undefined
    adjustment: number
  }[],
): Promise<any> => {
  try {
    const { data } = await storefrontCartServicesHttpClient.put(`/carts/${cartId}/update-items-quantity`, {
      lineItems,
    })
    return await getDataWithProductAndInventory(data)
  } catch (e) {
    console.error('Failed while fetching updateItemQuantity(), API - `/carts/${cartId}/update-item-quantity`', e)
  }
  return ''
}

export const removeItem = async (cartId: string, lineItemId: string): Promise<string> => {
  try {
    const { data } = await storefrontCartServicesHttpClient.put(`/carts/${cartId}/remove-item`, {
      lineItemId,
    })
    return await getDataWithProductAndInventory(data)
  } catch (e) {
    console.error('Failed while fetching removeItemQyantity(), API - `/carts/${cartId}/remove-item', e)
  }
  return ''
}

export const removeDiscountCode = async (cartId: string): Promise<string> => {
  try {
    const { data } = await storefrontCartServicesHttpClient.put(`/carts/${cartId}/remove-discount-code`, {})
    return await getDataWithProductAndInventory(data)
  } catch (e) {
    console.error('Failed while fetching removeDiscountCode(), API - `/carts/${cartId}/remove-discount-code', e)
  }
  return ''
}

export const markCartComplete = async (cartId: string): Promise<string> => {
  try {
    const { data } = await storefrontCartServicesHttpClient.put(`/carts/${cartId}/mark-cart-complete`, {})
    return await getDataWithProductAndInventory(data)
  } catch (e) {
    console.error('Failed while fetching markCartComplete(), API - `/carts/${cartId}/mark-cart-complete', e)
  }
  return ''
}

export const applyDiscountCode = async (cartId: string, discountCode: string): Promise<string> => {
  try {
    const { data } = await storefrontCartServicesHttpClient.put(`/carts/${cartId}/apply-discount-code`, {
      discountCode,
    })
    return await getDataWithProductAndInventory(data)
  } catch (e) {
    console.error('Failed while fetching applyDiscountCode(), API - `/carts/${cartId}/apply-discount-code', e)
  }
  return ''
}

export const addItemToCart = async (cartId: string, lineItems: LineItems) => {
  try {
    const { data } = await storefrontCartServicesHttpClient.put(`/carts/${cartId}/add-item`, {
      lineItems,
    })

    return await getDataWithProductAndInventory(data)
  } catch (e) {
    console.error('Failed while fetching addItemToCart(), API - `/carts/${cartId}/add-item`', e)
  }
  return ''
}

export const updateDateOfBirth = async (cartId: string, dob = '') => {
  try {
    const { data } = await storefrontCartServicesHttpClient.put(`/carts/${cartId}/update-date-of-birth`, {
      dateOfBirth: formatToYYYYMMDD(dob),
    })
    return data
  } catch (e) {
    console.error('Failed while fetching addItemToCart(), API - `/carts/${cartId}/add-item`', e)
    const quinceError = new QuinceApiError({
      error: e as Error,
      message: 'Failed on addToCart',
      apiName: 'DOB_ERROR',
    })
    throw quinceError
  }
}

export const getOrCreateCheckout = async (cartId: string) => {
  try {
    const { data } = await storefrontCartServicesHttpClient.put(`/carts/${cartId}/get-or-create-checkout`, {})

    return data
  } catch (e) {
    console.error('Failed while fetching getOrCreateCheckout(), API - `/carts/${cartId}/get-or-create-checkout`', e)
    // eslint-disable-next-line i18next/no-literal-string
    throw new Error('getOrCreateCheckout Failed ')
  }
}

export const buyNowCartApi = async (
  lineItems: LineItems,
  parentCartAuthMapping?: {
    id: string
    authCode: string
  },
): Promise<TBuyNowApiResponse | undefined> => {
  try {
    const { data } = await storefrontCartServicesHttpClient.post(`/carts/buy-now-cart`, {
      lineItems,
      parentCartAuthMapping,
    })
    return data
  } catch (e) {
    console.error('Failed while fetching buyNowCart(), API - `/carts/buy-now-cart`', e)
  }
  return
}

export const updateGiftingDetails = async (cartId: string, giftingDetails: GiftingDetails): Promise<any> => {
  try {
    const res = await storefrontCartServicesHttpClient.put(`/carts/${cartId}/update-gifting-details`, {
      recipientEmail: giftingDetails.recipientEmail,
      recipientName: giftingDetails.recipientName,
      senderName: giftingDetails.senderName,
      giftMessage: giftingDetails.giftMessage,
    })
    setCartAuthCodeFromHeader(res)
    const { data } = res
    return await getDataWithProductAndInventory(data)
  } catch (e) {
    console.error('Failed while updating gifting API - `/update-gifting-details`', e)
    throw e
  }
}

export const updateGiftingFlag = async (cartId: string, isGift: boolean): Promise<any> => {
  try {
    const res = await storefrontCartServicesHttpClient.put(`/carts/${cartId}/update-gifting-flag?isGift=${isGift}`, {})
    setCartAuthCodeFromHeader(res)
    const { data } = res
    return await getDataWithProductAndInventory(data)
  } catch (e) {
    console.error('Failed while updating gifting API - `/update-gifting-flag`', e)
    throw e
  }
}

export const removeAllOutOfStockItemsFromCart = async (cartId: string): Promise<any> => {
  try {
    const { data } = await storefrontCartServicesHttpClient.put(`/carts/${cartId}/remove-all-out-of-stock-items`, {})
    const cart = await getDataWithProductAndInventory(data)
    updateNewCheckoutInLocalStorage(cart)
    return cart
  } catch (e) {
    console.error(
      'Failed while removing all out of stock items API - `/carts/${cartId}/remove-all-out-of-stock-items`',
      e,
    )
    throw e
  }
}

export const updateSubscription = async (
  cartId: string,
  lineItemId: string,
  planId: number | null,
  intent: string,
): Promise<any> => {
  try {
    const payload = {
      subscriptionPlanId: planId,
      subscriptionIntent: intent,
    }
    const { data } = await storefrontCartServicesHttpClient.patch(
      `/carts/${cartId}/update-line-item/${lineItemId}`,
      payload,
    )
    return await getDataWithProductAndInventory(data)
  } catch (e) {
    console.error(
      `Failed while fetching updateSubscription(), API - /carts/${cartId}/update-line-item/${lineItemId}`,
      e,
    )
  }
}
